import {Injectable} from "@angular/core";
import {AuthService} from "@webmate-web-core/shared/services/auth.service";
import { HttpClient, HttpParams } from "@angular/common/http";
import {
    ReportInfo,
    ReportOutputType,
    ReportSharingToken,
    ReportTemplate, ReportTemplateType,
    ReportUpdateAction
} from '../../../../report/types';
import {Observable} from "rxjs";
import {GenericApiResponse} from "@webmate-web-core/shared/api/apibase.api";
import {BlobId} from "@webmate-web-core/shared/types/ids";
import {DateTime} from "@webmate-web-core/shared/types/base-types";
import {Report, ReportId, ReportOutput} from "../../../../report/report_types";
import {map} from "rxjs/operators";

@Injectable({ providedIn: 'root' })
export class ReportApi {
    constructor(public OAuth2AuthService: AuthService, private http: HttpClient, private authService: AuthService) {}

    getReport(id: ReportId): Observable<Report> {
        return this.http.get<Report>(`/report/reports/${id}`);
    }

    getReportInfo(id: ReportId): Observable<ReportInfo> {
        return this.http.get<ReportInfo>(`/report/reports/${id}/info`);
    }

    getReportInfosForProject(projectId: string): Observable<ReportInfo[]> {
        return this.http.get<ReportInfo[]>(`/report/projects/${projectId}/reports`);
    }

    getBlobUrl(reportId: ReportId, blobId: BlobId, asAttachment?: boolean, attachmentName?: string): string {
        const attachmentSuffix = asAttachment ? `?asAttachment=true&attachmentName=${attachmentName || 'attachment'}` : '';
        return this.authService.makeAuthorizedUrl('/report/reports/' + reportId + '/blobs/' + blobId) + attachmentSuffix;
    }



    /**
     * Retrieve report content.
     */
    getReportContentBlob(id: ReportId, blobId: BlobId): Observable<Blob> {
        return this.http.get(`/report/reports/${id}/blobs/${blobId}`, {responseType: 'blob'});
    }

    updateReport(id: ReportId, content: ReportUpdateAction[]): Observable<GenericApiResponse> {
        return this.http.post<GenericApiResponse>(`/report/reports/${id}`, content);
    }

    deleteReport(id: ReportId): Observable<GenericApiResponse> {
        return this.http.delete<GenericApiResponse>(`/report/reports/${id}`);
    }

    publishReport(id: ReportId): Observable<GenericApiResponse> {
        return this.http.post<GenericApiResponse>(`/report/reports/${id}/publish`, {});
    }

    getReportSharingTokens(id: ReportId): Observable<ReportSharingToken[]> {
        return this.http.get<ReportSharingToken[]>(`/report/reports/${id}/sharing-tokens`);
    }

    createSharingToken(id: ReportId, name: string, expirationTime?: DateTime): Observable<ReportSharingToken> {
        return this.http.post<ReportSharingToken>(`/report/reports/${id}/sharing-tokens`, {name, expirationTime});
    }

    deleteSharingToken(id: ReportId, tokenId: string): Observable<GenericApiResponse> {
        return this.http.delete<GenericApiResponse>(`/report/reports/${id}/sharing-tokens/${tokenId}`);
    }

    generateOutput(id: ReportId, outputType: ReportOutputType): Observable<ReportOutput> {
        return this.http.post<ReportOutput>(`/report/reports/${id}/output/${outputType}`, {});
    }

    getReportPreviewUrl(id: ReportId): Observable<string> {
        return this.http.get<any>(`/report/reports/${id}/preview-url`).pipe(map(({previewUrl}) => previewUrl));
    }

    /**
     * Report Templates
     */

    getReportTemplatesForProject(projectId: string, templateType?: ReportTemplateType, includeBaseProject: boolean = true): Observable<ReportTemplate[]> {
        let requestUrl = `/projects/${projectId}/report-templates?includeBaseProject=${includeBaseProject}`;
        if (templateType) {
            requestUrl += `&onlyOfType=${templateType}`;
        }
        return this.http.get<ReportTemplate[]>(requestUrl);
    }

    createReportTemplate(projectId: string, template: Omit<ReportTemplate, 'id' | 'isBaseTemplate'>): Observable<ReportTemplate> {
        return this.http.post<ReportTemplate>(`/projects/${projectId}/report-templates`, template);
    }

    updateReportTemplate(projectId: string, templateId: string, template: Partial<ReportTemplate>): Observable<ReportTemplate> {
        return this.http.put<ReportTemplate>(`/projects/${projectId}/report-templates/${templateId}`, template);
    }

    deleteReportTemplate(projectId: string, templateId: string): Observable<GenericApiResponse> {
        return this.http.delete<GenericApiResponse>(`/projects/${projectId}/report-templates/${templateId}`);
    }
}
